import { normalizeCtaImageAspectRatio } from '@/lib/embed/ctaImagePresentation';

export type PreviewSurface = 'question' | 'end-screen';
export type EmailSignupType = 'button' | 'embed';

export interface EmailSignupPreviewConfig {
  enabled: boolean;
  signup_type: EmailSignupType;
  title: string;
  description: string;
  button_cta: string;
  confirmation_message: string;
  signup_url: string;
  embed_code: string;
}

const PREVIEW_SURFACE_SET = new Set<PreviewSurface>(['question', 'end-screen']);
const SIGNUP_TYPE_SET = new Set<EmailSignupType>(['button', 'embed']);

function sanitizeString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

export function sanitizePreviewSurface(value: unknown): PreviewSurface | null {
  if (typeof value !== 'string') return null;
  return PREVIEW_SURFACE_SET.has(value as PreviewSurface) ? (value as PreviewSurface) : null;
}

export function sanitizeEmailSignupPreviewConfig(value: unknown): EmailSignupPreviewConfig | null {
  if (!value || typeof value !== 'object') return null;

  const input = value as Partial<EmailSignupPreviewConfig>;

  if (typeof input.enabled !== 'boolean') return null;
  if (!input.signup_type || !SIGNUP_TYPE_SET.has(input.signup_type)) return null;

  const title = sanitizeString(input.title);
  const description = sanitizeString(input.description);
  const buttonCta = sanitizeString(input.button_cta);
  const confirmationMessage = sanitizeString(input.confirmation_message);
  const signupUrl = sanitizeString(input.signup_url);
  const embedCode = sanitizeString(input.embed_code);

  if (
    title === null ||
    description === null ||
    buttonCta === null ||
    confirmationMessage === null ||
    signupUrl === null ||
    embedCode === null
  ) {
    return null;
  }

  return {
    enabled: input.enabled,
    signup_type: input.signup_type,
    title,
    description,
    button_cta: buttonCta,
    confirmation_message: confirmationMessage,
    signup_url: signupUrl,
    embed_code: embedCode,
  };
}

export function hasEmailSignupRenderableContent(
  config: EmailSignupPreviewConfig | null | undefined
): config is EmailSignupPreviewConfig {
  if (!config || !config.enabled) return false;
  if (config.signup_type === 'embed') return !!config.embed_code.trim();
  return !!config.signup_url.trim();
}

// Sponsorship preview (tagline + end CTA; used by portal postMessage and embed)
export interface SponsorshipPreviewConfig {
  sponsored_by_enabled: boolean;
  sponsored_by_text: string;
  sponsored_by_color: string;
  sponsored_by_url: string;
  cta_enabled: boolean;
  cta_tagline: string;
  cta_title: string;
  cta_description: string;
  cta_button_text: string;
  cta_url: string;
  logo_storage_bucket: string | null;
  logo_storage_path: string | null;
  cta_image_storage_bucket: string | null;
  cta_image_storage_path: string | null;
  cta_image_aspect_ratio: number | null;
}

function sanitizeOptionalString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function sanitizeOptionalNullString(value: unknown): string | null {
  if (value == null) return null;
  const s = typeof value === 'string' ? value.trim() : '';
  return s === '' ? null : s;
}

function sanitizeOptionalBoolean(value: unknown): boolean {
  return typeof value === 'boolean' ? value : false;
}

export function sanitizeSponsorshipPreviewConfig(value: unknown): SponsorshipPreviewConfig | null {
  if (!value || typeof value !== 'object') return null;
  const input = value as Record<string, unknown>;
  return {
    sponsored_by_enabled: sanitizeOptionalBoolean(input.sponsored_by_enabled),
    sponsored_by_text: sanitizeOptionalString(input.sponsored_by_text),
    sponsored_by_color: sanitizeOptionalString(input.sponsored_by_color),
    sponsored_by_url: sanitizeOptionalString(input.sponsored_by_url),
    cta_enabled: sanitizeOptionalBoolean(input.cta_enabled),
    cta_tagline: sanitizeOptionalString(input.cta_tagline),
    cta_title: sanitizeOptionalString(input.cta_title),
    cta_description: sanitizeOptionalString(input.cta_description),
    cta_button_text: sanitizeOptionalString(input.cta_button_text),
    cta_url: sanitizeOptionalString(input.cta_url),
    logo_storage_bucket: sanitizeOptionalNullString(input.logo_storage_bucket),
    logo_storage_path: sanitizeOptionalNullString(input.logo_storage_path),
    cta_image_storage_bucket: sanitizeOptionalNullString(input.cta_image_storage_bucket),
    cta_image_storage_path: sanitizeOptionalNullString(input.cta_image_storage_path),
    cta_image_aspect_ratio: normalizeCtaImageAspectRatio(input.cta_image_aspect_ratio),
  };
}

export function hasSponsorshipTaglineContent(
  config: SponsorshipPreviewConfig | null | undefined
): boolean {
  if (!config) return false;
  return !!(config.sponsored_by_enabled && config.sponsored_by_text?.trim());
}
